import { type ReactNode, useEffect, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconCircleCheckFilled, IconMail, IconPhone } from '@tabler/icons-react';
import { OpendoorLogoBadge } from 'components/landing-pages-v2/shared/OpendoorLogo';
import { useRouter } from 'next/router';

import { type OfferConfirmation, readOfferConfirmation } from './offerConfirmationHandoff';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const CARD = 'w-full flex-col gap-4 rounded-[16px] border border-od-border-primary p-4';
const CARD_TITLE = 'font-medium text-[18px] tracking-[-0.432px] text-od-content-primary';

const TimelineItem = ({
  state,
  last = false,
  title,
  detail,
}: {
  state: 'done' | 'current' | 'upcoming';
  last?: boolean;
  title: string;
  detail: string;
}) => (
  <Row className="items-stretch gap-3">
    <Col className="w-5 items-center">
      {state === 'done' ? (
        <IconCircleCheckFilled className="size-5 shrink-0 text-od-content-success" />
      ) : (
        <Box
          className={cn(
            'flex size-5 shrink-0 items-center justify-center rounded-full',
            state === 'current' ? 'bg-od-bg-success/10' : undefined,
          )}
        >
          <Box
            className={cn(
              'size-2.5 rounded-full',
              state === 'current' ? 'bg-od-bg-success' : 'bg-od-bg-inactive-secondary',
            )}
          />
        </Box>
      )}
      {last ? null : <Box className="w-px flex-1 bg-od-border-primary" />}
    </Col>
    <Col className={cn('gap-0.5', last ? undefined : 'pb-4')}>
      <Text className="font-medium text-body-md text-od-content-primary">{title}</Text>
      <Text className="text-body-sm text-od-content-secondary">{detail}</Text>
    </Col>
  </Row>
);

const OfferRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <Row className="items-start justify-between gap-4">
    <Text className="text-body-md text-od-content-secondary">{label}</Text>
    <Text className="text-right font-medium text-body-md text-od-content-primary">{children}</Text>
  </Row>
);

/**
 * The `/make-offer/confirm` page — "Your offer is in!" per design. Everything
 * it shows arrives through the sessionStorage handoff written at submission;
 * opened without one (a direct visit, the legacy-reserved route) it returns
 * to the start, as the legacy portal did.
 */
/** The design shows the street on its own line, the city-state-zip below. */
const addressLines = (address: string): string[] => {
  const commaAt = address.indexOf(',');
  if (commaAt === -1) {
    return [address];
  }
  return [address.slice(0, commaAt), address.slice(commaAt + 1).trim()];
};

const OfferConfirmationPage = () => {
  const router = useRouter();
  // Read after mount: sessionStorage is client-only, and reading it during the
  // initial render would diverge from the server HTML.
  const [confirmation, setConfirmation] = useState<OfferConfirmation | undefined>(undefined);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const stored = readOfferConfirmation();
    setConfirmation(stored);
    setChecked(true);
    if (!stored) {
      void router.replace('/make-offer/start');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  if (!checked || !confirmation) {
    return <Box className="min-h-[50vh] w-full bg-od-bg-primary" />;
  }

  return (
    <Box as="section" className="w-full justify-center bg-od-bg-primary px-6 pt-14 pb-28">
      <Col className="w-full max-w-[608px] items-center gap-6">
        <Col className="items-center gap-4">
          <Box className="flex size-14 items-center justify-center rounded-[12px] bg-od-bg-success/10">
            <IconCircleCheckFilled className="size-7 text-od-content-success" />
          </Box>
          <Text
            as="h1"
            className="text-center font-medium text-[28px] tracking-[-0.784px] text-od-content-primary"
          >
            Your offer is in!
          </Text>
          <Text className="text-center text-body-md text-od-content-secondary">
            We’ll be in touch within 24 hours. You can reach your Listing Partner anytime using the
            info below.
          </Text>
        </Col>

        <Row className={cn(CARD, 'flex-row items-center')}>
          <Box
            className={cn(
              'size-[120px] shrink-0 overflow-hidden rounded-[8px]',
              !confirmation.photoUrl && 'bg-od-bg-inactive-secondary',
            )}
          >
            {confirmation.photoUrl ? (
              <Image src={confirmation.photoUrl} alt="" className="size-full object-cover" />
            ) : null}
          </Box>
          <Col className="flex-1 gap-3">
            <OfferRow label="Address">
              {addressLines(confirmation.address).map((line, index) => (
                <Text as="span" key={line} className={cn('block', index > 0 && 'font-normal')}>
                  {line}
                </Text>
              ))}
            </OfferRow>
            <Box className="h-px w-full bg-od-border-primary" />
            <OfferRow label="Your offer">{usd.format(confirmation.priceCents / 100)}</OfferRow>
          </Col>
        </Row>

        <Col className={CARD}>
          <Text as="h2" className={CARD_TITLE}>
            What happens next
          </Text>
          <Col>
            <TimelineItem
              state="done"
              title="Offer received"
              detail={`Added as offer #${confirmation.offerId} just now`}
            />
            <TimelineItem
              state="current"
              title="We’ll review your offer"
              detail="We’ll check terms and uploaded documents"
            />
            <TimelineItem
              state="upcoming"
              last
              title="Your Listing Partner will reach out to you"
              detail="They’ll follow up with next steps"
            />
          </Col>
        </Col>

        <Col className={CARD}>
          <Row className="items-start justify-between gap-4">
            <Col className="gap-1">
              <Text className="font-medium text-body-md text-od-content-primary">
                {confirmation.partnerName ?? 'Opendoor'}
              </Text>
              <Text className="text-body-sm text-od-content-secondary">Your Listing Partner</Text>
              <Col className="gap-1.5 pt-2">
                {confirmation.partnerPhone ? (
                  <Row className="items-center gap-2">
                    <IconPhone className="size-4 text-od-content-secondary" />
                    <Text className="text-body-sm text-od-content-primary">
                      {confirmation.partnerPhone}
                    </Text>
                  </Row>
                ) : null}
                <Row className="items-center gap-2">
                  <IconMail className="size-4 text-od-content-secondary" />
                  <Text className="text-body-sm text-od-content-primary">
                    {confirmation.partnerEmail}
                  </Text>
                </Row>
              </Col>
            </Col>
            <Box className="flex size-10 shrink-0 items-center justify-center rounded-full border border-od-border-primary text-od-content-accent-brand">
              <OpendoorLogoBadge width="12px" />
            </Box>
          </Row>
        </Col>

        <Col className="w-full gap-3">
          <Button
            href="/make-offer/start"
            variant="secondary"
            className="w-full rounded-[12px]"
            aria-label="Submit another offer"
            analyticsName="cosmos-make-offer-confirm-submit-another"
          >
            Submit another offer
          </Button>
        </Col>
      </Col>
    </Box>
  );
};

export default OfferConfirmationPage;
